import { t } from '@lingui/macro';
import { connect } from 'react-redux';
import styles from '../FilterSidebar.module.css';
import CompositeLegenStyles from './CompositeLegend.module.css';

const CompositeLegenComponent = ({ terrainMode }) => {
    if (terrainMode !== 'composite') {
        return null;
    }
    const palette = {
        n: { 5: '#98b581', 20: '#8dc458', 40: '#84d600' },
        ne: { 5: '#72a890', 20: '#3dab71', 40: '#00ab44' },
        e: { 5: '#7c8ead', 20: '#5078b7', 40: '#0068c0' },
        se: { 5: '#8c75a0', 20: '#77479d', 40: '#6c00a3' },
        s: { 5: '#b47ba1', 20: '#c04d9c', 40: '#ca009c' },
        sw: { 5: '#cb8b8f', 20: '#e76f7a', 40: '#ff5568' },
        w: { 5: '#c5a58a', 20: '#e2a66c', 40: '#ffab47' },
        nw: { 5: '#bdbf89', 20: '#d6db5e', 40: '#f4fa00' },
    };
    const directions = [
        { name: 'N', angle: 270, dir: 'n' },
        { name: 'NE', angle: 315, dir: 'ne' },
        { name: 'E', angle: 0, dir: 'e' },
        { name: 'SE', angle: 45, dir: 'se' },
        { name: 'S', angle: 90, dir: 's' },
        { name: 'SW', angle: 135, dir: 'sw' },
        { name: 'W', angle: 180, dir: 'w' },
        { name: 'NW', angle: 225, dir: 'nw' },
    ];
    const slopeRanges = [
        { radius1: 38, radius2: 48, slopeKey: 5, label: t`< 5°` },
        { radius1: 24, radius2: 38, slopeKey: 20, label: t`5-20°` },
        { radius1: 0, radius2: 24, slopeKey: 40, label: t`20-40°` },
        { radius1: 0, radius2: 24, slopeKey: 40, label: t`> 40°` },
    ];
    const centerX = 100;
    const centerY = 100;
    const hexToRgba = (hex, alpha) => {
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };
    const renderCompositeSegments = () => {
        const segments = [];
        slopeRanges.forEach((slope, slopeIdx) => {
            directions.forEach((dir, dirIdx) => {
                const nextDir = directions[(dirIdx + 1) % directions.length];
                const startAngle = (dir.angle - 22.5) * (Math.PI / 180);
                const endAngle = (nextDir.angle - 22.5) * (Math.PI / 180);
                const color = palette[dir.dir][slope.slopeKey];
                const x1Outer = centerX + slope.radius2 * Math.cos(startAngle);
                const y1Outer = centerY + slope.radius2 * Math.sin(startAngle);
                const x2Outer = centerX + slope.radius2 * Math.cos(endAngle);
                const y2Outer = centerY + slope.radius2 * Math.sin(endAngle);
                const x1Inner = centerX + slope.radius1 * Math.cos(startAngle);
                const y1Inner = centerY + slope.radius1 * Math.sin(startAngle);
                const x2Inner = centerX + slope.radius1 * Math.cos(endAngle);
                const y2Inner = centerY + slope.radius1 * Math.sin(endAngle);
                const largeArc = 0;
                const pathData = [
                    `M ${x1Inner} ${y1Inner}`,
                    `L ${x1Outer} ${y1Outer}`,
                    `A ${slope.radius2} ${slope.radius2} 0 ${largeArc} 1 ${x2Outer} ${y2Outer}`,
                    `L ${x2Inner} ${y2Inner}`,
                    `A ${slope.radius1} ${slope.radius1} 0 ${largeArc} 0 ${x1Inner} ${y1Inner}`,
                    'Z',
                ].join(' ');

                segments.push(
                    <path
                        key={`segment-${dir.name}-${slope.label}`}
                        d={pathData}
                        fill={hexToRgba(color, 0.75)}
                        stroke="none"
                    />
                );
            });
        });
        return segments;
    };
    const renderDirectionLabels = () => {
        const offsets = {
            'N': { x: 0, y: -2 },
            'NE': { x: 2, y: -1 },
            'E': { x: 2, y: 1 },
            'SE': { x: 2, y: 2 },
            'S': { x: 0, y: 3 },
            'SW': { x: -2, y: 2 },
            'W': { x: -2, y: 1 },
            'NW': { x: -2, y: -1 },
        };
        return directions.map(dir => {
            const angle = dir.angle * (Math.PI / 180);
            const labelRadius = 62;
            const x = centerX + labelRadius * Math.cos(angle) + offsets[dir.name].x;
            const y = centerY + labelRadius * Math.sin(angle) + offsets[dir.name].y;
            const isCardinal = dir.name.length === 1;
            return (
                <text
                    key={`label-${dir.name}`}
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className={CompositeLegenStyles.directionLabel}
                    fontSize="9"
                    fontWeight={isCardinal ? 'bold' : 'normal'}
                    fill="#fff"
                    stroke="#333"
                    strokeWidth="0.3"
                    paintOrder="stroke"
                >
                    {dir.name}
                </text>
            );
        });
    };
    const renderSlopeLabels = () => {
        // Все подписи расположены в южном сегменте (внизу), друг под другом
        // S сегмент: 90° ± 22.5° = 67.5° - 112.5°, середина = 90°
        const southAngle = 90 * (Math.PI / 180);
        const slopeLabelsData = [
            // 5-20° - первая подпись
            { radius: 43, label: slopeRanges[1].label },
            // 20-40° - вторая подпись
            { radius: 31, label: slopeRanges[2].label },
            // > 40° - третья подпись
            { radius: 18, label: slopeRanges[3].label },
        ];

        return slopeLabelsData.map((item, idx) => {
            const x = centerX + item.radius * Math.cos(southAngle);
            const y = centerY + item.radius * Math.sin(southAngle);
            return (
                <text
                    key={`slope-label-${idx}`}
                    x={x}
                    y={y}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    className={CompositeLegenStyles.slopeAngleLabel}
                    fontSize="8"
                    fill="#fff"
                    stroke="#333"
                    strokeWidth="0.2"
                    paintOrder="stroke"
                >
                    {item.label}
                </text>
            );
        });
    };
    return (
        <div className={CompositeLegenStyles.wrapper}>
            <div className={styles.legHead}>{t`Aspect with Slope`}:</div>
            <div className={CompositeLegenStyles.container}>
                <svg
                    width="220"
                    height="220"
                    className={CompositeLegenStyles.circleSvg}
                    viewBox="20 20 160 160"
                >
                    {renderCompositeSegments()}
                    {renderDirectionLabels()}
                    {renderSlopeLabels()}
                </svg>
            </div>
        </div>
    );
};
const mapStateToProps = ({ map: { terrainMode } }) => ({ terrainMode });

export const CompositeLegend = connect(mapStateToProps)(CompositeLegenComponent);
